import { Box, Divider, Flex } from '@chakra-ui/react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  checkOperationPermission,
  checkPermission,
  PermissionsEnum,
} from '../../../../../utils'
import { allowExtensionOperations } from '../../../../../utils/mediaLibrary'
import { ACLPermissionEnum } from '../../../../constants'
import type { MediaLibrarySearchFileResponse } from '../../../../interfaces'
import type { FileDetailsResponse } from '../../../../interfaces/FileDetailsResponse'
import { UserContext } from '../../../../providers'
import { PublicLinksInfoContext } from '../../../../providers/PublicLinksInfoProvider'
import EmbeddedMetadata from './EmbeddedMetadata'
import FileInfo from './FileInfo'
import OtherOperations from './OtherOperations'
import Tags from './Tags'

interface AssetSummaryProps {
  fileDetails: FileDetailsResponse | MediaLibrarySearchFileResponse
}

const AssetSummary: React.FC<AssetSummaryProps> = ({ fileDetails }) => {
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)

  const [details, setDetails] = useState<
    FileDetailsResponse | MediaLibrarySearchFileResponse
  >(fileDetails)

  useEffect(() => {
    setDetails(fileDetails)
  }, [fileDetails])

  const [isTagsModalOpen, toggleTagsModal] = useState(false)

  const onPatched = (patch: Partial<FileDetailsResponse>): void => {
    setDetails(prev => ({ ...(prev as FileDetailsResponse), ...patch }))
  }

  const canShowOtherOperation = useMemo(
    () =>
      !isPublicLink &&
      allowExtensionOperations(fileDetails) &&
      checkOperationPermission({
        user,
        aclPermission: [ACLPermissionEnum.CONTRIBUTE, ACLPermissionEnum.MANAGE],
        selectedFiles: [fileDetails as MediaLibrarySearchFileResponse],
        selectedFolders: [],
        selectedMediaCollections: [],
      }),
    [fileDetails, user],
  )

  const showTagsSection = useMemo<boolean>(() => {
    if (isPublicLink) {
      return false
    }
    const d = details as FileDetailsResponse
    return (
      (d.tags?.length ?? 0) > 0 ||
      (d.AITags?.length ?? 0) > 0 ||
      checkPermission({
        user,
        permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
      })
    )
  }, [details, user, isPublicLink])

  if (!details) {
    return null
  }

  return (
    <Flex direction="column" width="100%">
      <Box p="16px">
        <FileInfo fileDetails={details as FileDetailsResponse} />
      </Box>

      <Divider borderColor="imagekitGray.300" />

      {canShowOtherOperation && (
        <>
          <Box p="16px">
            <OtherOperations fileDetails={details as FileDetailsResponse} />
          </Box>
          <Divider borderColor="imagekitGray.300" />
        </>
      )}

      {showTagsSection && (
        <>
          <Box p="16px">
            <Tags
              fileDetails={details as FileDetailsResponse}
              isTagsModalOpen={isTagsModalOpen}
              toggleTagsModal={toggleTagsModal}
              onPatched={onPatched}
            />
          </Box>
          <Divider borderColor="imagekitGray.300" />
        </>
      )}

      <Box p="16px">
        <EmbeddedMetadata data={fileDetails.embeddedMetadata} />
      </Box>
    </Flex>
  )
}

export interface MLFileDetailRouteParams {
  fileId: string
  versionId?: string
  mediaCollectionId?: string
}

export default AssetSummary
